import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", parent_team_id: "none", description: "" });

  const load = async () => {
    const { data } = await api.get("/teams");
    setTeams(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/teams", {
        name: form.name,
        parent_team_id: form.parent_team_id === "none" ? null : form.parent_team_id,
        description: form.description || null,
      });
      toast.success("Team created");
      setOpen(false); setForm({ name: "", parent_team_id: "none", description: "" });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Team removed");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const roots = teams.filter(t => !t.parent_team_id);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow">Structure</div>
          <h1 className="font-display text-4xl mt-1">Teams & sub-teams</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-1"/>New team
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create team</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="eyebrow">Name</Label>
                <Input required value={form.name} data-testid="team-name-input"
                  onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-2 h-11 rounded-lg"/>
              </div>
              <div>
                <Label className="eyebrow">Parent team (optional)</Label>
                <Select value={form.parent_team_id} onValueChange={(v)=>setForm({...form, parent_team_id: v})}>
                  <SelectTrigger data-testid="team-parent-select" className="mt-2 h-11 rounded-lg"><SelectValue placeholder="None - top-level team"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None - top-level team</SelectItem>
                    {roots.map((t)=> <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="eyebrow">Description</Label>
                <Input value={form.description}
                  onChange={(e)=>setForm({...form, description:e.target.value})} className="mt-2 h-11 rounded-lg"/>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="team-submit-btn"
                  className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        {roots.length === 0 && <div className="text-sm text-[#4B5773]">No teams yet.</div>}
        <ul className="divide-y divide-slate-100" data-testid="teams-list">
          {roots.map((t) => (
            <li key={t.team_id} className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{t.name}</div>
                  {t.description && <div className="text-xs text-[#4B5773] mt-0.5">{t.description}</div>}
                </div>
                <Button variant="ghost" size="sm" onClick={()=>del(t.team_id)} data-testid={`team-del-${t.team_id}`}>
                  <Trash2 className="h-4 w-4"/>
                </Button>
              </div>
              <ul className="mt-3 ml-4 space-y-2">
                {teams.filter(s=>s.parent_team_id===t.team_id).map((s)=> (
                  <li key={s.team_id} className="flex items-center justify-between bg-[#EEF3FB] rounded-lg px-3 py-2">
                    <span className="text-sm">↳ {s.name}</span>
                    <Button variant="ghost" size="sm" onClick={()=>del(s.team_id)} data-testid={`team-del-${s.team_id}`}><Trash2 className="h-4 w-4"/></Button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
